<template>
	<div>
		<div class="t-page-header">
			<div>
				<router-link to="/tasks/projects" style="font-size:13px; color:var(--t-text-2);">← פרויקטים</router-link>
				<h1 style="margin-top:6px;">
					<span class="t-avatar lg" :style="{background: project?.color, marginInlineEnd:'10px'}">{{ icon(project?.icon) }}</span>
					{{ project?.name || "..." }}
				</h1>
				<div class="t-subtitle">{{ project?.description }}</div>
			</div>
			<button class="t-btn primary" @click="openCreate">+ משימה חדשה</button>
		</div>

		<div class="t-toolbar">
			<input v-model="filters.q" placeholder="חיפוש..." @input="debouncedLoad" />
			<select v-model="filters.assignee_id" @change="load">
				<option value="">כל האחראים</option>
				<option v-for="u in users" :key="u.id" :value="u.id">{{ u.name }}</option>
			</select>
		</div>

		<div class="t-board">
			<div v-for="col in columns" :key="col.key" :class="['t-column','col-' + col.key]" @dragover.prevent @drop="dropTo(col.key, $event)">
				<div class="t-col-head">
					<span>{{ col.label }}</span>
					<span class="t-count">{{ grouped[col.key]?.length || 0 }}</span>
				</div>
				<div
					v-for="t in (grouped[col.key] || [])"
					:key="t.id"
					class="t-task-card"
					draggable="true"
					@dragstart="dragStart(t, $event)"
					@click="openTask(t.id)"
				>
					<button
						class="t-check on-card"
						:class="{ done: t.status === 'done' }"
						draggable="false"
						:title="t.status === 'done' ? 'בטל סימון הושלם' : 'סמן כהושלם'"
						@click.stop="toggleDone(t)"
						@mousedown.stop
					>✓</button>
					<span class="t-priority" :class="'p-' + t.priority">{{ priorityLabel(t.priority) }}</span>
					<h4>{{ t.title }}</h4>
					<div class="t-card-foot">
						<span v-if="t.assignee_id" class="t-avatar sm" :style="{background: t.assignee_color || '#6366f1'}" :title="t.assignee_name">
							{{ initials(t.assignee_name) }}
						</span>
						<span v-else style="color:var(--t-text-3); font-size:11px;">לא משוייך</span>
						<span class="t-deadline" :class="{late: overdue(t)}">
							<template v-if="t.deadline">📅 {{ formatDate(t.deadline) }}</template>
						</span>
						<span v-if="+t.comments_count">💬 {{ t.comments_count }}</span>
					</div>
				</div>
				<div v-if="!grouped[col.key]?.length" style="text-align:center; color:var(--t-text-3); padding:24px 8px; font-size:12px;">גרור משימות לכאן</div>
			</div>
		</div>

		<TaskModal v-if="modal.open" :task-id="modal.taskId" :project-id="projectId" :lock-project="true" :user="user" :users="users" :projects-list="projectsList" @close="modal.open = false" @saved="load" />
	</div>
</template>

<script>
import { TasksAPI, TasksRealtime, initials, formatDate, isOverdue } from "./TasksApi";
import TaskModal from "./TaskModal.vue";

const ICON_MAP = { folder: "📁", rocket: "🚀", target: "🎯", chart: "📊", bug: "🐞", design: "🎨", code: "💻", task: "✅" };

export default {
	name: "TasksProject",
	components: { TaskModal },
	props: { user: Object, users: Array, openTaskId: { type: Number, default: 0 }, projectsList: { type: Array, default: () => [] } },
	emits: ["task-opened"],
	data() {
		return {
			project: null,
			tasks: [],
			filters: { q: "", assignee_id: "" },
			modal: { open: false, taskId: 0 },
			dragId: null,
			subscription: null,
			debouncedLoad: () => {},
			columns: [
				{ key: "todo",        label: "להתחלה" },
				{ key: "in_progress", label: "בעבודה" },
				{ key: "review",      label: "בבדיקה" },
				{ key: "done",        label: "הושלם" },
				{ key: "cancelled",   label: "בוטל" },
			],
		};
	},
	computed: {
		projectId() { return +this.$route.params.id; },
		grouped() {
			const g = {};
			this.columns.forEach(c => g[c.key] = []);
			for (const t of this.tasks) {
				if (!g[t.status]) g[t.status] = [];
				g[t.status].push(t);
			}
			return g;
		},
	},
	async mounted() {
		this.debouncedLoad = this.debounce(this.load, 350);
		// Parallel loads — was sequential
		await Promise.all([this.loadProject(), this.load(), this.subscribeRealtime(this.projectId)]);
		if (this.openTaskId) {
			this.modal.taskId = this.openTaskId;
			this.modal.open = true;
			this.$emit("task-opened");
		}
	},
	beforeUnmount() {
		if (this.subscription) TasksRealtime.unsubscribe(this.subscription);
	},
	watch: {
		openTaskId(v) {
			if (v) {
				this.modal.taskId = v;
				this.modal.open = true;
				this.$emit("task-opened");
			}
		},
		"$route.params.id"(newId, oldId) {
			if (newId === oldId) return;
			if (this.subscription) TasksRealtime.unsubscribe(this.subscription);
			Promise.all([this.loadProject(), this.load(), this.subscribeRealtime(newId)]);
		},
	},
	methods: {
		initials, formatDate,
		icon(k) { return ICON_MAP[k] || "📁"; },
		debounce(fn, t = 250) { let h; return (...a) => { clearTimeout(h); h = setTimeout(() => fn(...a), t); }; },
		overdue(t) { return isOverdue(t.deadline, t.status); },
		priorityLabel(p) { return { low: "נמוכה", medium: "בינונית", high: "גבוהה", urgent: "דחוף" }[p] || p; },
		async loadProject() {
			const res = await TasksAPI.projectGet(this.projectId);
			this.project = res?.project || null;
		},
		async subscribeRealtime(projectId) {
			const channel = "tasks-project-" + projectId;
			this.subscription = await TasksRealtime.subscribe(channel, {
				"task.created":   (d) => this.applyTaskEvent(d, "upsert"),
				"task.updated":   (d) => this.applyTaskEvent(d, "upsert"),
				"task.status":    (d) => this.applyTaskEvent(d, "patch"),
				"task.assigned":  (d) => this.applyTaskEvent(d, "patch"),
				"task.deleted":   (d) => this.applyTaskEvent(d, "delete"),
				"task.commented": (d) => this.applyTaskEvent(d, "comment"),
			});
		},
		applyTaskEvent(data, kind) {
			if (!data) return;
			// Skip events triggered by this user — local state is already correct.
			if (data.by && this.user && +data.by === +this.user.id && kind !== "delete") return;

			if (kind === "delete") {
				this.tasks = this.tasks.filter(t => +t.id !== +data.task_id);
				return;
			}
			if (kind === "comment") {
				const t = this.tasks.find(x => +x.id === +data.task_id);
				if (t) t.comments_count = (+t.comments_count || 0) + 1;
				return;
			}
			if (kind === "patch") {
				const t = this.tasks.find(x => +x.id === +data.task_id);
				if (t) {
					if (data.status !== undefined)      t.status = data.status;
					if (data.assignee_id !== undefined) t.assignee_id = +data.assignee_id;
				} else {
					this.scheduleReload();
				}
				return;
			}
			// upsert (created / updated)
			if (data.task) {
				const idx = this.tasks.findIndex(x => +x.id === +data.task.id);
				if (idx >= 0) this.tasks.splice(idx, 1, { ...this.tasks[idx], ...data.task });
				else this.scheduleReload();
			} else {
				this.scheduleReload();
			}
		},
		scheduleReload() {
			if (this._reloadTimer) return;
			this._reloadTimer = setTimeout(() => {
				this._reloadTimer = null;
				this.load();
			}, 400);
		},
		async load() {
			const filters = { project_id: this.projectId };
			if (this.filters.q) filters.q = this.filters.q;
			if (this.filters.assignee_id) filters.assignee_id = this.filters.assignee_id;
			const res = await TasksAPI.tasksList(filters);
			this.tasks = (res && res.items) || [];
		},
		openCreate() { this.modal.taskId = 0; this.modal.open = true; },
		openTask(id) { this.modal.taskId = id; this.modal.open = true; },
		async toggleDone(t) {
			const next = t.status === "done" ? "todo" : "done";
			const prev = t.status;
			t.status = next; // optimistic — grouped recomputes, card moves column
			const res = await TasksAPI.taskStatus(t.id, next);
			if (res && res.error) t.status = prev;
		},
		dragStart(t, e) { this.dragId = t.id; e.dataTransfer.effectAllowed = "move"; },
		async dropTo(status, e) {
			e.preventDefault();
			if (!this.dragId) return;
			const t = this.tasks.find(x => x.id == this.dragId);
			if (t && t.status !== status) {
				t.status = status;
				await TasksAPI.taskStatus(this.dragId, status);
			}
			this.dragId = null;
		},
	},
};
</script>
